import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { validate } from "../../../utils/validate";
import styles from "./SignUp.module.css";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import { notify } from "../toast";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword, sendEmailVerification, updateProfile } from "firebase/auth";
import { auth, firebaseEmailContinueUrl, hasFirebaseConfig } from "../../../config/firebase";
import AuthShell from "../AuthShell";

/** Map non-standard HTML `name`s → React state keys used by validate() / Firebase. */
const HTML_NAME_TO_FIELD = {
  dblog_signup_name: "name",
  dblog_signup_mail: "email",
  dblog_signup_pass: "password",
  dblog_signup_pass2: "confirmPassword",
  IsAccepted: "IsAccepted",
};

const resolveField = (htmlName) => HTML_NAME_TO_FIELD[htmlName] || htmlName;

const SignUp = () => {
  const { t } = useTranslation();
  const [data, setData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    IsAccepted: false,
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [dirty, setDirty] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Prevent password-manager paint until the user intentionally focuses the field.
  const [passwordReadOnly, setPasswordReadOnly] = useState(true);
  const [confirmReadOnly, setConfirmReadOnly] = useState(true);

  useEffect(() => {
    setErrors(validate(data, "signUp"));
  }, [data]);

  const changeHandler = (event) => {
    const field = resolveField(event.target.name);
    if (field === "IsAccepted") {
      setData({ ...data, [field]: event.target.checked });
      setTouched((prev) => ({ ...prev, [field]: true }));
    } else {
      setData({ ...data, [field]: event.target.value });
      setDirty((prev) => ({
        ...prev,
        [field]: Boolean(prev[field]) || event.target.value.trim().length > 0,
      }));
    }
  };

  const blurHandler = (event) => {
    const field = resolveField(event.target.name);
    if (dirty[field]) {
      setTouched((prev) => ({ ...prev, [field]: true }));
    }
  };

  const getFirebaseSignUpError = (errorCode) => {
    switch (errorCode) {
      case "auth/email-already-in-use":
        return t("auth.emailAlreadyInUse");
      case "auth/invalid-email":
        return t("auth.invalidEmail");
      case "auth/weak-password":
        return t("auth.weakPassword");
      case "auth/network-request-failed":
        return t("auth.networkError");
      default:
        return t("auth.createAccountError");
    }
  };

  const submitHandler = (event) => {
    event.preventDefault();
    const validationErrors = validate(data, "signUp");
    setErrors(validationErrors);

    if (!hasFirebaseConfig || !auth) {
      notify(t("auth.firebaseNotConfigured"), "error");
      return;
    }

    if (!Object.keys(validationErrors).length && !isSubmitting) {
      const pushData = async () => {
        setIsSubmitting(true);
        try {
          const credential = await createUserWithEmailAndPassword(
            auth,
            data.email.toLowerCase(),
            data.password
          );

          if (data.name.trim()) {
            await updateProfile(credential.user, { displayName: data.name.trim() });
          }

          await sendEmailVerification(credential.user, {
            url: firebaseEmailContinueUrl,
          });

          notify(t("auth.accountCreated"), "success");
          navigate("/email-sent", { state: { email: data.email.toLowerCase() } });
        } catch (error) {
          notify(getFirebaseSignUpError(error?.code), "error");
        } finally {
          setIsSubmitting(false);
        }
      };
      pushData();
    } else {
      notify(t("auth.checkFieldsAgain"), "error");
      setTouched({
        name: true,
        email: true,
        password: true,
        confirmPassword: true,
        IsAccepted: true,
      });
    }
  };

  const navigate = useNavigate();

  const handleOnClick = () => {
    navigate("/");
  };

  return (
    <AuthShell subtitle={t("auth.brandPanelSignup")}>
      <form
        className={styles.formLogin}
        onSubmit={submitHandler}
        autoComplete="off"
        data-lpignore="true"
        data-1p-ignore="true"
        data-bwignore="true"
        data-form-type="other"
      >
        <h2>{t("auth.signUp")}</h2>
        <p className={styles.formLead}>{t("auth.signupLead")}</p>

        {/* Decoy fields: absorb password-manager autofill so real fields stay empty. */}
        <div className={styles.autofillDecoy} aria-hidden="true">
          <input
            type="text"
            name="username"
            tabIndex={-1}
            autoComplete="username"
            defaultValue=""
          />
          <input
            type="password"
            name="password"
            tabIndex={-1}
            autoComplete="current-password"
            defaultValue=""
          />
        </div>

        <div>
          <label className={styles.fieldLabel} htmlFor="signup-name">
            {t("auth.name")}
          </label>
          <div
            className={
              errors.name && touched.name
                ? styles.unCompleted
                : !errors.name && touched.name
                  ? styles.completed
                  : undefined
            }
          >
            <input
              id="signup-name"
              type="text"
              name="dblog_signup_name"
              value={data.name}
              placeholder={t("auth.name")}
              onChange={changeHandler}
              onBlur={blurHandler}
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
            />
            <img src={process.env.PUBLIC_URL + "/img/auth/user.svg"} alt="" />
          </div>
          {errors.name && touched.name && <span className={styles.error}>{errors.name}</span>}
        </div>
        <div>
          <label className={styles.fieldLabel} htmlFor="signup-email">
            {t("auth.email")}
          </label>
          <div
            className={
              errors.email && touched.email
                ? styles.unCompleted
                : !errors.email && touched.email
                  ? styles.completed
                  : undefined
            }
          >
            <input
              id="signup-email"
              type="text"
              inputMode="email"
              name="dblog_signup_mail"
              value={data.email}
              placeholder={t("auth.email")}
              onChange={changeHandler}
              onBlur={blurHandler}
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
            />
            <img src={process.env.PUBLIC_URL + "/img/auth/email.svg"} alt="" />
          </div>
          {errors.email && touched.email && <span className={styles.error}>{errors.email}</span>}
        </div>
        <div>
          <label className={styles.fieldLabel} htmlFor="signup-password">
            {t("auth.password")}
          </label>
          <div
            className={
              errors.password && touched.password
                ? styles.unCompleted
                : !errors.password && touched.password
                  ? styles.completed
                  : undefined
            }
          >
            <input
              id="signup-password"
              type="password"
              name="dblog_signup_pass"
              value={data.password}
              placeholder={t("auth.password")}
              onChange={changeHandler}
              onBlur={blurHandler}
              autoComplete="new-password"
              readOnly={passwordReadOnly}
              onFocus={() => setPasswordReadOnly(false)}
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
            />
            <img src={process.env.PUBLIC_URL + "/img/auth/password.svg"} alt="" />
          </div>
          {errors.password && touched.password && (
            <span className={styles.error}>{errors.password}</span>
          )}
        </div>
        <div>
          <label className={styles.fieldLabel} htmlFor="signup-confirm">
            {t("auth.confirmPassword")}
          </label>
          <div
            className={
              errors.confirmPassword && touched.confirmPassword
                ? styles.unCompleted
                : !errors.confirmPassword && touched.confirmPassword
                  ? styles.completed
                  : undefined
            }
          >
            <input
              id="signup-confirm"
              type="password"
              name="dblog_signup_pass2"
              value={data.confirmPassword}
              placeholder={t("auth.confirmPassword")}
              onChange={changeHandler}
              onBlur={blurHandler}
              autoComplete="new-password"
              readOnly={confirmReadOnly}
              onFocus={() => setConfirmReadOnly(false)}
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
            />
            <img src={process.env.PUBLIC_URL + "/img/auth/password.svg"} alt="" />
          </div>
          {errors.confirmPassword && touched.confirmPassword && (
            <span className={styles.error}>{errors.confirmPassword}</span>
          )}
        </div>
        <div>
          <div className={styles.terms}>
            <input
              type="checkbox"
              name="IsAccepted"
              checked={data.IsAccepted}
              id="accept"
              onChange={changeHandler}
            />
            <label htmlFor="accept">{t("auth.acceptTerms")}</label>
          </div>
          {errors.IsAccepted && touched.IsAccepted && (
            <span className={styles.error}>{errors.IsAccepted}</span>
          )}
        </div>
        <div>
          <div className={styles.actions}>
            <button className={styles.primaryBtn} type="submit" disabled={isSubmitting}>
              {isSubmitting ? t("auth.creating") : t("auth.createAccount")}
            </button>
            <button className={styles.secondaryBtn} type="button" onClick={handleOnClick}>
              {t("auth.cancel")}
            </button>
          </div>
          <p className={styles.footerLink}>
            {t("auth.alreadyHaveAccount")} <Link to="/login">{t("auth.signIn")}</Link>
          </p>
        </div>
      </form>
      <ToastContainer />
    </AuthShell>
  );
};

export default SignUp;
